import { produce } from 'immer';
import { useCallback, useRef, useState } from 'react';

import { enabled } from '../../../lib/who-am-i';
import { DimensionKey, getQualityScores, loadQualityScore, QualityScoreAPIData } from '../../../queries/recognition-analysis';

const TOTAL_DIMENSIONS = Object.keys(DimensionKey).length;
const MIN_TEXT_LENGTH = 30;
const DEBOUNCE_DELAY = 1_000;

enum Status {
	Idle,
	Loading,
	Complete,
}

type QualityQuery = {
	text: string;
	status: Status;
};

type CachedScore = { text: string; data: QualityScoreAPIData };

const blankDimensions = Object.values(DimensionKey).map((key) => ({
	key,
	matches: false,
	reason: '',
}));

export const useScoreRecognition = () => {
	const isAIGratitudeEnabled = enabled('recognition-quality-scoring-ui');
	const [dimensions, setDimensions] = useState(blankDimensions);
	const [text, setText] = useState('');
	const [isFetching, setIsFetching] = useState(false);
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

	const queueRef = useRef(
		new (class QualityScoreQueue {
			queue: QualityQuery[] = [];

			add(text: string) {
				// Clear existing debounce timer
				if (debounceTimerRef.current) {
					clearTimeout(debounceTimerRef.current);
					debounceTimerRef.current = null;
				}

				// Set up debounced processing
				debounceTimerRef.current = setTimeout(() => {
					this.queueNextQuery(text);
				}, DEBOUNCE_DELAY);
			}

			queueNextQuery(text: string) {
				// If there are multiple items in queue, only keep the latest one
				if (this.queue.length > 0) {
					this.queue = [this.queue[0], { text, status: Status.Idle }];
				} else {
					this.queue.push({ text, status: Status.Idle });
				}

				this.process();
			}

			process() {
				if (!this.queue.length) {
					setIsFetching(false);
					return;
				}

				if (!isAIGratitudeEnabled) {
					this.queue.shift();
					this.process();
					return;
				}

				const query = this.queue[0];

				switch (query.status) {
					case Status.Idle:
						this.fetchNext();
						break;
					case Status.Loading:
						break;
					case Status.Complete:
						this.queue.shift();
						this.process();
						break;
				}
			}

			async fetchNext() {
				const query = this.queue[0];
				const trimmedText = query.text.trim();
				// Test the text without any mentions or amounts (lazy style)
				const cleanedText = trimmedText.replace(/\+\d+/, '').replaceAll(/@[^ ]+/g, '');

				// If text is too short, return blank dimensions
				if (cleanedText.length < MIN_TEXT_LENGTH) {
					setText('');
					setDimensions(blankDimensions);
					query.status = Status.Complete;
					this.process();
					return;
				}

				setIsFetching(true);
				query.status = Status.Loading;

				try {
					const timeoutPromise = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 20000));
					const data = await Promise.race([loadQualityScore(trimmedText), timeoutPromise]);
					const resolved = await normalizeData(data, trimmedText);

					setText(trimmedText);
					setDimensions(resolved.dimensions);
					query.status = Status.Complete;
				} catch (e) {
					// On error or timeout, don't change data, just move to next queue item
					query.status = Status.Complete;
				}

				this.process();
			}
		})()
	);

	const handleChange = useCallback((newText: string) => {
		queueRef.current.add(newText);
	}, []);

	return {
		dimensions,
		isFetching,
		onChange: handleChange,
		text,
		totalDimensions: TOTAL_DIMENSIONS,
		totalMatches: dimensions.filter((d) => d.matches).length,
	};
};

export const normalizeData = async (data: QualityScoreAPIData, trimmedText: string) => {
	const existing = await getQualityScores();

	return (
		existing
			// Make the data easier to work with
			.map(([[_key, text], data]) => ({ text, data }) as CachedScore)
			// Only look at items where the text analyzed is a segment of the current text
			.filter((item) => trimmedText.includes(item.text))
			// Longer text takes precedent
			.sort((a, b) => b.text.length - a.text.length)
			// For simplicity, now map to just the QualityScoreAPIData, which is the same interface as the `data` variable above
			.map((item) => item.data)
			// Now we start with the data we received. We then look back through our data and replace any dimension that doesn't
			// match with the first one we find that does
			.reduce((acc, curr) => {
				return produce(acc, (draft) => {
					draft.dimensions.forEach((dimension, index) => {
						if (!dimension.matches) {
							const found = curr.dimensions.find((prev) => prev.key === dimension.key);

							if (found && found.matches) {
								draft.dimensions[index] = found;
							}
						}
					});
				});
			}, data)
	);
};
